import React, { Fragment, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

import createField from 'client/components/FormContent/Field/createField';
import EnhanceForm from 'client/components/FormContent/FieldList/EnhanceFormList';
import FormButtons from 'editor/components/Sections/FormButtons';
import LoadingClient from 'common/components/LoadingClient';
import { RECAPTCHA_SITE_KEY } from 'config/envVariable';
import { getOptionLimitCount } from 'common/utils/config';

function FormList(props) {
    const {
        formId,
        sponsorId,
        title,
        sectionList,
        fieldDataList,
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldTouched,
        setFieldError,
        setFieldValue,
        setValues,
        setTouched,
        isResult,
        settingStyle,
        validateForm,
        onError = () => {},
        isSubmitting,
        promoSerialNo,
        isSuspendPostFormMode,
        enableRecaptCha,
        shouldResetMultiChoice,
        onAfterResetMultiChoice,
        options
    } = props;
    const { i18n } = useTranslation();
    const optionSetting = useSelector(state => state.settings.options);
    const [endPostFormSuspend, setEndPostFormSuspend] = useState(false);
    const updateFormValue = newFieldValue => {
        const newValue = { ...values, ...newFieldValue };
        setValues(newValue);
    };
    const recaptchaRef = useRef();
    // eslint-disable-next-line
    const verify = e => {
        e && e.preventDefault();
        if (isSuspendPostFormMode && !promoSerialNo) {
            window.parent.postMessage({ submitted: true }, '*');
        } else {
            validateForm().then(fieldErrors => {
                setTouchedAll();
                if (Object.keys(fieldErrors).length === 0) {
                    if (enableRecaptCha) {
                        recaptchaRef.current.execute();
                    } else {
                        submit();
                    }
                } else {
                    onError(fieldErrors);
                }
            });
        }
    };

    const submit = () => {
        if (enableRecaptCha) {
            const recaptchaToken = recaptchaRef.current.getValue();
            if (recaptchaToken) {
                setValues({ ...values, recaptchaToken });
                recaptchaRef.current.reset();
            }
        }
        handleSubmit({ sponsorId });
    };

    const reloadRecaptcha = () => {
        recaptchaRef.current.reset();
    };

    const setTouchedAll = () => {
        const touchedFields = Object.keys(values).reduce((obj, key) => {
            obj[key] = true;
            return obj;
        }, {});
        setTouched(touchedFields);
    };

    useEffect(() => {
        if (isSuspendPostFormMode && promoSerialNo && !endPostFormSuspend) {
            verify();
            setEndPostFormSuspend(true);
        }
    }, [verify, isSuspendPostFormMode, promoSerialNo, endPostFormSuspend]);

    useEffect(() => {
        if (!shouldResetMultiChoice) return;
        const optionSettingMap = optionSetting.reduce((acc, item) => {
            acc.set(item.id, item);
            return acc;
        }, new Map());

        // 處理多選題組自動重設
        let hasReset = false;
        const newValues = { ...values };

        fieldDataList.forEach(field => {
            // 僅處理多選題組（type 301/302）且 multiChoice
            if ((field.type === 301 || field.type === 302) && field.multiChoice) {
                // 取得該欄位的 options
                const fieldOptions = options
                    .filter(opt => opt.fieldId === field.id && !opt.deleted)
                    .map(opt => ({
                        ...opt,
                        setting: optionSettingMap.get(opt.id)
                    }));

                // 檢查 value
                const selected = values[field.id] || [];
                if (Array.isArray(selected) && selected.length > 0) {
                    // 過濾掉 submissionLimit === 0 的選項
                    const filtered = selected.filter(sel => {
                        const opt = fieldOptions.find(o => o.id === sel.optionId);
                        if (!opt) return true;

                        // 需同時 enableOptionsLimit、showRest 為 true 且 submissionLimit === 0
                        if (
                            field.enableOptionsLimit &&
                            field.showRest &&
                            getOptionLimitCount(opt) === 0
                        ) {
                            hasReset = true;
                            return false;
                        }
                        return true;
                    });

                    if (filtered.length !== selected.length) {
                        newValues[field.id] = filtered;

                        if (hasReset) {
                            setValues(newValues);
                        }

                        // 執行完成後呼叫回調
                        if (onAfterResetMultiChoice) {
                            onAfterResetMultiChoice();
                        }
                    }
                }
            }
        });
    }, [
        shouldResetMultiChoice,
        fieldDataList,
        values,
        setValues,
        onAfterResetMultiChoice,
        optionSetting,
        options
    ]);

    const fieldIdsOfThisPage = [];
    const mergedData = sectionList
        .map(section => {
            if (!isResult && section.type === 501) {
                return false;
            }
            const { id: sectionId } = section;
            const fieldsOfSection = fieldDataList.filter(field => {
                const isTarget = field.sectionId === sectionId;
                if (isTarget) {
                    fieldIdsOfThisPage.push(field.id);
                }
                return isTarget;
            });
            const fieldsValue = fieldsOfSection.reduce((obj, field) => {
                obj[field.id] = values[field.id];
                return obj;
            }, {});
            const fieldsError = fieldsOfSection.reduce((errorList, field) => {
                if (errors[field.id]) {
                    errorList.push(errors[field.id]);
                }
                return errorList;
            }, []);
            return {
                ...section,
                fieldsOfSection,
                fieldsValue,
                fieldsError
            };
        })
        .filter(s => s);
    function validateIsPageCanChange() {
        return validateForm().then(fieldErrors => {
            const hasError = fieldIdsOfThisPage.some(id => fieldErrors[id]);
            if (hasError) {
                setTouched(
                    fieldIdsOfThisPage.reduce((obj, id) => {
                        obj[id] = true;
                        return obj;
                    }, {})
                );
                onError(fieldErrors);
            }
            return !hasError;
        });
    }

    return (
        <Fragment>
            {isSubmitting && <LoadingClient />}
            <form css={css(formStyle)} id={formId} name={title} onSubmit={e => verify(e)}>
                {!isResult && enableRecaptCha && (
                    <ReCAPTCHA
                        css={css(reCAPTCHAStyle)}
                        ref={recaptchaRef}
                        size="invisible"
                        onChange={submit}
                        onExpired={reloadRecaptcha}
                        onErrored={reloadRecaptcha}
                        sitekey={RECAPTCHA_SITE_KEY}
                        hl={i18n.language}
                    />
                )}
                {mergedData.map(({ fieldsOfSection, fieldsValue, fieldsError, ...sectionData }) => (
                    <Fragment key={sectionData.id}>
                        {createField({
                            ...sectionData,
                            settingStyle,
                            value: fieldsValue,
                            error: (() => {
                                if (fieldsError.length > 1) {
                                    return fieldsError[1];
                                }
                                return fieldsError[0];
                            })(),
                            touched: fieldsOfSection[0] && touched[fieldsOfSection[0].id],
                            handleChange,
                            handleBlur,
                            updateFormValue,
                            setFieldTouched,
                            setFieldError,
                            setFieldValue,
                            isRequired: fieldsOfSection[0] && fieldsOfSection[0].isRequired
                        })}
                    </Fragment>
                ))}
                <FormButtons show={!isResult} isPageCanChange={validateIsPageCanChange} />
            </form>
        </Fragment>
    );
}

FormList.propTypes = {
    formId: PropTypes.number,
    sponsorId: PropTypes.number,
    title: PropTypes.string,
    sectionList: PropTypes.array,
    fieldDataList: PropTypes.array,
    values: PropTypes.object,
    touched: PropTypes.object,
    errors: PropTypes.object,
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    handleSubmit: PropTypes.func,
    setFieldTouched: PropTypes.func,
    setFieldError: PropTypes.func,
    setFieldValue: PropTypes.func,
    setTouched: PropTypes.func,
    setValues: PropTypes.func,
    isResult: PropTypes.bool,
    settingStyle: PropTypes.object,
    validateForm: PropTypes.func,
    onError: PropTypes.func,
    isSubmitting: PropTypes.bool,
    promoSerialNo: PropTypes.number,
    isSuspendPostFormMode: PropTypes.bool,
    enableRecaptCha: PropTypes.bool,
    shouldResetMultiChoice: PropTypes.bool,
    onAfterResetMultiChoice: PropTypes.func,
    options: PropTypes.array
};

export default EnhanceForm(FormList);

const formStyle = `
    position: relative;
`;

const reCAPTCHAStyle = `
    .grecaptcha-badge {
        z-index: 999;
        margin-bottom: 25px;
    }
`;
